/**
 * @file     gtagDelivery.ts
 * @readme   ../CLAUDE.md
 * @purpose  Decides which leg emits a lead conversion. GA4 does not deduplicate
 *           `generate_lead` (it has no event-level dedup outside `purchase`, and
 *           gtag drops `event_id` on the wire), so the browser leg and the server
 *           Measurement Protocol leg must be mutually exclusive rather than
 *           "both fire and let GA4 collapse them".
 */
'use client';

import { env } from '@/core/config/env';

type GtagState = 'ready' | 'blocked' | 'loading' | undefined;

/**
 * gtag fans every `event` call out to EVERY configured target, and the Google Ads
 * tag forwards to the linked GA4 property, so an unscoped event lands in GA4 twice.
 * Every event we emit must name the GA4 measurement id as its only destination;
 * anything that genuinely belongs in Ads opts in by passing its own `send_to`.
 */
export function ga4Only<T extends Record<string, unknown> | undefined>(
  params: T,
): Record<string, unknown> {
  const base = params ?? {};
  if ('send_to' in base) return base;
  return env.gaMeasurementId ? { ...base, send_to: env.gaMeasurementId } : base;
}

declare global {
  interface Window {
    __reltaGtag?: 'ready' | 'blocked' | 'loading';
  }
}

/** How long to let an in-flight gtag.js settle before handing the emission to the server. */
const SETTLE_TIMEOUT_MS = 900;
const POLL_MS = 50;

function readState(): GtagState {
  return typeof window === 'undefined' ? undefined : window.__reltaGtag;
}

/**
 * Ask the deferred gtag loader to run now. GtagBoot loads gtag.js on the
 * first scroll/click/touch, so by submit time it has almost always settled; this
 * covers the visitor who converts without ever tripping one of those listeners.
 */
export function forceGtagLoad(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new Event('relta:lead-submitted'));
}

/**
 * True when the browser will deliver a GA4 hit, which means the server must NOT
 * fire its Measurement Protocol backstop for the same lead.
 *
 * Resolves immediately in the settled cases. Only an in-flight load waits, and
 * only up to `SETTLE_TIMEOUT_MS`; an unresolved load counts as blocked so the
 * server leg covers it. Under-counting is never a risk here because exactly one
 * of the two legs is authorised on every path.
 */
export async function willBrowserEmitGa4(): Promise<boolean> {
  if (typeof window === 'undefined' || !env.enableAnalytics || !env.gaMeasurementId) return false;

  forceGtagLoad();
  if (readState() === 'ready') return true;
  if (readState() === 'blocked') return false;

  const deadline = Date.now() + SETTLE_TIMEOUT_MS;
  while (Date.now() < deadline) {
    await new Promise((resolve) => setTimeout(resolve, POLL_MS));
    const state = readState();
    if (state === 'ready') return true;
    if (state === 'blocked') return false;
  }
  return false;
}
